import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { APIIcon } from '@dev/ee-components';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '@material-ui/core';
import { links } from '@/services/dictionaries/links';
import { SettingsPaths, APIPaths } from '@/services/dictionaries/Paths';
import { SaveOnboardingStart, getOnboardingStore } from '@/store/onboarding/actions';
import { OnboardingVideoThumbnail } from '@/components/Onboarding/components/OnboardingVideoThumbnail';
import { YT_GENERATE_API_KEY_ID } from '@/services/constants';
import { OnboardingTask, OnboardingSteps, OnboardingTaskProps } from '../../../types';
import SvgApiDocumentation from './svgs/svg-api-documentation.svg';
import SvgApiReport from './svgs/svg-api-report.svg';
import SvgDocumentation from '../../../svgs/customicon-documentation.svg';
import SvgLibrary from '../../../svgs/customicon-library.svg';

const taskId = 'sendEmailsViaApiTask';

export enum SendEmailsViaApiTaskSavedDataKeysEnum {
    SendEmailsViaApiTaskConnectWithYourTool = 'SendEmailsViaApiTaskConnectWithYourTool',
    SendEmailsViaApiTaskViewEmailLogs = 'SendEmailsViaApiTaskViewEmailLogs',
    SendEmailsViaApiTaskSkipStep1 = 'SendEmailsViaApiTaskSkipStep1',
    SendEmailsViaApiTaskSkipStep2 = 'SendEmailsViaApiTaskSkipStep2',
    SendEmailsViaApiTaskSkipStep3 = 'SendEmailsViaApiTaskSkipStep3',
}

export const useSendEmailsViaApiTask = ({ onboardingGoals }: OnboardingTaskProps): OnboardingTask => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { savedData, data } = useSelector(getOnboardingStore);

    const goToDomains = (): void => {
        dispatch(push(SettingsPaths.Domains));
    };

    const goToCreateApi = (): void => {
        dispatch(push(SettingsPaths.CreateAPI));
    };

    const goToEmailLogs = (): void => {
        if (!savedData?.SendEmailsViaApiTaskViewEmailLogs) {
            dispatch(SaveOnboardingStart({ SendEmailsViaApiTaskViewEmailLogs: true }));
        }
        dispatch(push(APIPaths.LogsEmail));
    };

    const handleConnectWithToolDone = () => {
        dispatch(SaveOnboardingStart({ SendEmailsViaApiTaskConnectWithYourTool: true }));
    };

    const useSendEmailsViaApiSteps: OnboardingSteps = {
        id: taskId,
        title: t('sendemailsviaapi'),
        items: [
            {
                title: t('createapikey'),
                features: [
                    t('sendemailsviaapi_createapikey_feature1'),
                    t('sendemailsviaapi_createapikey_feature2'),
                    <Trans
                        key="sendemailsviaapi_createapikey_feature3"
                        i18nKey="sendemailsviaapi_createapikey_feature3"
                        components={{ url: <Link onClick={goToDomains} /> }}
                    />,
                ],
                secondaryContent: <OnboardingVideoThumbnail videoId={YT_GENERATE_API_KEY_ID} />,
                primaryActionTitle: t('createapikey'),
                primaryActionCallback: goToCreateApi,
                isSkipped: !!savedData?.SendEmailsViaApiTaskSkipStep1,
                skipSavedDataKey: SendEmailsViaApiTaskSavedDataKeysEnum.SendEmailsViaApiTaskSkipStep1,
                isCompleted: data?.OneApiKey,
            },
            {
                title: t('connectwithyourtool'),
                features: [
                    t('sendemailsviaapi_connectwithyourtool_feature1'),
                    t('sendemailsviaapi_connectwithyourtool_feature2'),
                ],
                links: [
                    {
                        href: links.restApiDoc,
                        label: t('gotoapidocumentation'),
                        icon: <SvgDocumentation />,
                        target: '_blank',
                    },
                    {
                        href: links.apiLibraries,
                        label: t('gotoapilibrary'),
                        icon: <SvgLibrary />,
                        target: '_blank',
                    },
                ],
                secondaryContent: <SvgApiDocumentation />,
                primaryActionTitle: t('markasdone'),
                primaryActionCallback: handleConnectWithToolDone,
                isNotReady: !!savedData?.SendEmailsViaApiTaskSkipStep1 || !data?.OneApiKey,
                isSkipped: !!savedData?.SendEmailsViaApiTaskSkipStep2,
                skipSavedDataKey: SendEmailsViaApiTaskSavedDataKeysEnum.SendEmailsViaApiTaskSkipStep2,
                isCompleted: !!savedData?.SendEmailsViaApiTaskConnectWithYourTool,
            },
            {
                title: t('checklogs'),
                features: [
                    t('sendemailsviaapi_checklogs_feature1'),
                    <Trans
                        key="sendemailsviaapi_checklogs_feature2"
                        i18nKey="sendemailsviaapi_checklogs_feature2"
                        components={{ url: <Link href={links.whatisemaildeliverystatus} target="_blank" /> }}
                    />,
                ],
                secondaryContent: <SvgApiReport />,
                primaryActionTitle: t('viewemaillogs'),
                primaryActionCallback: goToEmailLogs,
                isNotReady:
                    !!savedData?.SendEmailsViaApiTaskSkipStep1 ||
                    !!savedData?.SendEmailsViaApiTaskSkipStep2 ||
                    !data?.OneApiKey,
                isSkipped: !!savedData?.SendEmailsViaApiTaskSkipStep3,
                skipSavedDataKey: SendEmailsViaApiTaskSavedDataKeysEnum.SendEmailsViaApiTaskSkipStep3,
                isCompleted: !!savedData?.SendEmailsViaApiTaskViewEmailLogs,
            },
        ],
    };

    return {
        header: t('sendemailsviaapi'),
        icon: <APIIcon />,
        title: t('delivertransactionalemailswithease'),
        description: (
            <Trans
                i18nKey="sendemailsviaapi_desc"
                components={{ url: <Link href={links.apiVsSmtp} target="_blank" /> }}
            />
        ),
        steps: [useSendEmailsViaApiSteps],
        activeStepsIndex: 0,
        isVisible: onboardingGoals.isConnectingViaAPIAnswered,
    };
};
